import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IFormbuilderFieldTypeConfiguration,
  IBaseConfigurationFormModel,
} from '../../../models/form-builder-types-configurations.model';
import {
  baseFieldParseIntoSettingsFormModel,
  baseFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import {
  getMaxLengthValidatorFormConfig,
  getMaxValidatorFormConfig,
  getMinLengthValidatorFormConfig,
  getMinValidatorFormConfig,
  getPatternValidatorFormConfig,
  getRequiredValidatorFormConfig,
  getValidationFieldsFormConfig,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-generator.util';
import { IFormFieldsValidationModel } from '../../../models/form-fields-formly-validation.model';
import {
  parseEmailValidatorParseFieldIntoModel,
  parseMaxLengthValidatorParseFieldIntoModel,
  parseMaxValidatorParseFieldIntoModel,
  parseMinLengthValidatorParseFieldIntoModel,
  parseMinValidatorParseFieldIntoModel,
  parsePatternValidatorParseFieldIntoModel,
  parseRequiredValidatorParseFieldIntoModel,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';
import {
  setRequiredValidatorUpdateFieldFromModel,
  setMinLengthValidatorUpdateFieldFromModel,
  setMaxLengthValidatorUpdateFieldFromModel,
  setPatternValidatorUpdateFieldFromModel,
  setEmailValidatorMessageIntoField,
  setMinValidatorUpdateFieldFromModel,
  setMaxValidatorUpdateFieldFromModel,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import { FormBuilderService } from '../../../services/form-builder.service';
import { getFieldDisplayFormConfig } from '../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import { IFormFieldsDisplayModel } from '../../../models/form-fields-formly-display.model';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { fieldsBaseConfigurationFormModel } from './shared.configuration';
import { getConditionalValidationsFormConfig } from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-generator.util';
import { EAvailableConditionalValidations } from '../../../models/form-fields-formly-conditional-validation.model';
import { parseConditionalValidatorsModelFromFieldIntoModel } from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-parse-to-model.util';
import { setConditionalExpressionFromModel } from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-set-from-model.util';

const defaultInputConfigFormModel: IBaseConfigurationFormModel = {
  ...fieldsBaseConfigurationFormModel(),
};

export const inputFieldGetConfigurationConfig = (
  formBuilderServiceInstance: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: defaultInputConfigFormModel,
      formlyForm: [
        ...getDefaultBaseSettingsConfigFormly(formBuilderServiceInstance),
      ],
    },
    validations: {
      formModel: {},
      formlyForm: [
        ...getInputFieldValidationConfiguration(formBuilderServiceInstance),
        getInputConditionalValidationConfiguration(formBuilderServiceInstance),
      ],
    },
    display: {
      formModel: {},
      formlyForm: [...getFieldDisplayFormConfig(formBuilderServiceInstance)],
    },
  };
};

export const inputFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): IBaseConfigurationFormModel => {
  return baseFieldParseIntoSettingsFormModel(field);
};

export const inputFieldUpdateFromSettingsFormModel = (
  model: IBaseConfigurationFormModel,
  inputField: FormlyFieldConfig
): FormlyFieldConfig => {
  return baseFieldUpdateFromSettingsFormModel(model, inputField);
};

export const inputFieldParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): IFormFieldsValidationModel => {
  let model: IFormFieldsValidationModel = {};

  model = parseRequiredValidatorParseFieldIntoModel(model, field);

  if (field?.props?.type === 'number') {
    model = parseMinValidatorParseFieldIntoModel(model, field);
    model = parseMaxValidatorParseFieldIntoModel(model, field);
  }

  if (field?.props?.type === 'customemail') {
    model = parseEmailValidatorParseFieldIntoModel(model, field);
  }

  if (!field?.props?.type) {
    model = parseMinLengthValidatorParseFieldIntoModel(model, field);
    model = parseMaxLengthValidatorParseFieldIntoModel(model, field);
    model = parsePatternValidatorParseFieldIntoModel(model, field);
  }

  model = parseConditionalValidatorsModelFromFieldIntoModel(model, field);

  return model;
};

export const inputFieldUpdateFromValidationFormModel = (
  model: IFormFieldsValidationModel,
  inputField: FormlyFieldConfig
): FormlyFieldConfig => {
  inputField = setRequiredValidatorUpdateFieldFromModel(model, inputField);

  if (inputField.props?.type === 'number') {
    inputField = setMinValidatorUpdateFieldFromModel(model, inputField);
    inputField = setMaxValidatorUpdateFieldFromModel(model, inputField);
  }

  if (inputField.props?.type === 'customemail') {
    inputField = setEmailValidatorMessageIntoField(model, inputField);
  }

  if (!inputField.props?.type) {
    inputField = setMinLengthValidatorUpdateFieldFromModel(model, inputField);
    inputField = setMaxLengthValidatorUpdateFieldFromModel(model, inputField);
    inputField = setPatternValidatorUpdateFieldFromModel(model, inputField);
  }

  inputField = setConditionalExpressionFromModel(model, inputField);

  return inputField;
};

export const inputFieldParseFieldIntoDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const inputFieldUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};

const getInputFieldValidationConfiguration = (
  formBuilderServiceInstance: FormBuilderService
) => {
  const configuredField: FormlyFieldConfig | null =
    formBuilderServiceInstance.configureFieldAction$.getValue();

  const validationConfigFields: FormlyFieldConfig[] = [
    getRequiredValidatorFormConfig(),
  ];

  if (configuredField?.props?.type === 'number') {
    validationConfigFields.push(
      ...[getMinValidatorFormConfig(), getMaxValidatorFormConfig()]
    );
  }

  if (!configuredField?.props?.type) {
    validationConfigFields.push(
      ...[
        getMinLengthValidatorFormConfig(),
        getMaxLengthValidatorFormConfig(),
        getPatternValidatorFormConfig(),
      ]
    );
  }

  validationConfigFields.push(
    getValidationFieldsFormConfig(formBuilderServiceInstance)
  );
  return validationConfigFields;
};

const getInputConditionalValidationConfiguration = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig => {
  const configuredField: FormlyFieldConfig | null =
    formBuilderServiceInstance.configureFieldAction$.getValue();

  if (
    configuredField?.props?.type == 'number' ||
    configuredField?.props?.type == 'customemail'
  ) {
    return getConditionalValidationsFormConfig(
      [EAvailableConditionalValidations.required],
      formBuilderServiceInstance
    );
  }

  return getConditionalValidationsFormConfig(
    [
      EAvailableConditionalValidations.required,
      EAvailableConditionalValidations.minLength,
      EAvailableConditionalValidations.maxLength,
      EAvailableConditionalValidations.patternValidation,
    ],
    formBuilderServiceInstance
  );
};
